import React from 'react'
import Link from 'next/link'
import SwiperCore, { Navigation, Pagination, EffectFade } from 'swiper'
import Cta from '../styles/Cta'
import styled, { css } from 'styled-components'
import { Item, Content } from '../styles/Banner'
import Grid, { Cell } from '../styles/Grid'
import { Component, FadeInArea } from '../styles/Component'
import { Parallax, ParallaxProvider } from 'react-scroll-parallax'
import { useInView } from 'react-intersection-observer'
import { lighter } from '../helpers/color'
import { breakpoint } from '../layout/styles'
import ReactHtmlParser from 'react-html-parser'

SwiperCore.use([Navigation, Pagination, EffectFade])

const Media = ({ item }: any) => (
  <>
    {(item.video_mp4 || item.video_webm) && (
      <video
        autoPlay={!item.show_video_controls}
        muted={!item.show_video_controls}
        loop={!item.show_video_controls}
        playsInline
        controls={item.show_video_controls}
        poster={item.image && item.image.url}
      >
        {item.video_webm && (
          <source src={item.video_webm.url} type="video/webm" />
        )}
        {item.video_mp4 && <source src={item.video_mp4.url} type="video/mp4" />}
      </video>
    )}
    {item.image?.url && !item.video_mp4 && !item.video_webm && (
      <MediaContent
        src={`${item.image.url}?height=${460}&auto=webp&format=png`}
        srcSet={`${item.image.url}?height=${460}&dpr=2&auto=webp&format=png 2x`}
        alt={item.image.description || 'Image'}
        width={760}
        height={460}
      />
    )}
  </>
)

const MixedContentBanner = ({ item }: any) => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.2 })
  const left = item.text_alignment === 'Left'
  const light = item.text_style === 'Light'
  const { blue, dark, ocean, white } = {
    blue: (item.cta_color === 'blue'),
    dark: (item.cta_color === 'dark'),
    ocean: (item.cta_color === 'ocean'),
    white: (item.cta_color === 'white'),
  }
  return (
    <div>
      <Component ref={ref}>
        <ParallaxProvider>
          <BannerItem
            light={light}
            style={{
              '--gradient': item.color?.[0]?.background,
              '--border-color': item.color?.[0]?.color
            }}
            left={left}
            variant="small"
          >
            <Wrapper variant="ingredient" inView={inView}>
              <Grid reverse={left}>
                <MediaContainer md={9} gap={left ? 2 : 0} left={left}>
                  <Media {...{ item }} />
                </MediaContainer>
                <Cell md={3} gap={left ? 0 : 2}>
                  <Container left={left}>
                    <div>
                      <ContentBannerTitle styleOverrides={item.title_style_overrides}>
                        {ReactHtmlParser(item.title)}
                      </ContentBannerTitle>
                      <ContentBannerDescription>
                        {ReactHtmlParser(item.description)}
                      </ContentBannerDescription>
                      {item.cta.href && (
                        <Link href={item.cta.href} passHref>
                          <Cta href="" light={light} blue={blue} dark={dark} ocean={ocean} white={white}>
                            {item.cta.title}
                          </Cta>
                        </Link>
                      )}
                    </div>
                  </Container>
                </Cell>
              </Grid>
            </Wrapper>
          </BannerItem>
        </ParallaxProvider>
      </Component>
    </div>
  )
}
export default MixedContentBanner

export const BannerItem = styled(Item)`
  padding: 1.5rem;
  height: auto !important;
  @media ${breakpoint.mobile} {
    --text: ${(props) => (props.light ? 'white' : 'black')};
  }
  @media ${breakpoint.desktop} {
    padding: 0;
  }
`

export const Container = styled.div<any>`
  display: flex;
  justify-content: center;
  flex-direction: column;
  height: 100%;
  margin: var(--mcb-margin ${(props) => (props.left ? '-reverse' : '')}, 0);
  padding-right: 2rem;
  h2 {
    margin-top: 2rem;
    @media ${breakpoint.desktop} {
      margin-top: 0;
    }
  }
  
  @media ${breakpoint.tablet} and ${breakpoint.desktop_down} {
    max-width: 85%;
    margin: 0 auto;
  }
`

export const MediaContainer = styled(Cell)`
  position: relative;
  border-bottom: var(--mcb-border, 0);
  flex: 5;
  @media ${breakpoint.desktop} {
    padding: 6rem 0;
    max-width: 100%;
    max-height: 100%;
    margin-left: 0;
    border-bottom: 0;
    border-${(props) => (props.left ? 'left' : 'right')}: var(--mcb-border, 0);
  }
  
  img {
    width: 100%;
    display: block;
    @media screen and ${breakpoint.tablet} {
      min-height: 250px;
    }
    object-fit: cover;
    max-height: 100%;
    @media ${breakpoint.desktop} {
      height: 100%;
    }
  }
  video {
    display: block;
    position: static;
    max-height: 460px;
  }
`

const ContentBannerTitle = styled.h2<any>`
  ${props => props.styleOverrides && props.styleOverrides.font_size && css`
    font-size: ${props.styleOverrides.font_size} !important;
  `}
  ${props => props.styleOverrides && props.styleOverrides.line_height && css`
    line-height: ${props.styleOverrides.line_height} !important;
  `}
`

const MediaContent = styled.img`
  height: auto;
  max-height: 460px;
  max-width: 100%;
`

const ContentBannerDescription = styled.p<any>`
  p {
    margin: 0;
  }
  sup {
    font-size: 10px;
    line-height: 13px;
  }
`

export const Wrapper = styled<any>(FadeInArea)`
  position: relative;
  width: 100%;
  ${(props) =>
    (!props.variant || props.variant !== 'full') &&
    css`
      max-width: 1120px;
      margin: 0 auto;
    `}
`
